import { Injectable, inject } from '@angular/core';
import {
  FieldFragment,
  GetContactGQL,
  GetCustomFieldGQL,
  GetGroupGQL,
  GetLeadGQL,
  GetSpecializationGQL,
  GetWorkplaceGQL,
} from '@graphql';
import { FieldWithValue } from '@shared-models';
import { ObjectName } from '@view-model';
import { Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class GetObjectService {
  private readonly getContact = inject(GetContactGQL);
  private readonly getGroup = inject(GetGroupGQL);
  private readonly getSpecialization = inject(GetSpecializationGQL);
  private readonly getLead = inject(GetLeadGQL);
  private readonly getWorkplace = inject(GetWorkplaceGQL);
  private readonly getCustomField = inject(GetCustomFieldGQL);

  getObjectDataWithFields(
    id: number,
    objectName: ObjectName,
    fields: FieldFragment[],
  ): Observable<FieldWithValue[]> {
    if (!id) {
      return of(fields);
    }
    switch (objectName) {
      case 'Contact':
        return this.getContact
          .watch({ variables: { id }, fetchPolicy: 'no-cache' })
          .valueChanges.pipe(
            filter((result) => !result.loading),
            map((data) => data.data.getContact),
            map((params) =>
              fields.map((field) => ({ ...field, value: (params as any)[field.name] })),
            ),
          );
      case 'Group':
        return this.getGroup
          .watch({ variables: { id }, fetchPolicy: 'no-cache' })
          .valueChanges.pipe(
            filter((result) => !result.loading),
            map((data) => data.data.getGroup),
            map((params) =>
              fields.map((field) => ({ ...field, value: (params as any)[field.name] })),
            ),
          );
      case 'Specialization':
        return this.getSpecialization
          .watch({ variables: { id }, fetchPolicy: 'no-cache' })
          .valueChanges.pipe(
            filter((result) => !result.loading),
            map((data) => data.data.getSpecialization),
            map((params) =>
              fields.map((field) => ({ ...field, value: (params as any)[field.name] })),
            ),
          );
      case 'Lead':
        return this.getLead.watch({ variables: { id }, fetchPolicy: 'no-cache' }).valueChanges.pipe(
          filter((result) => !result.loading),
          map((data) => data.data.getLead),
          map((params) =>
            fields.map((field) => ({ ...field, value: (params as any)[field.name] })),
          ),
        );
      case 'Workplace':
        return this.getWorkplace
          .watch({ variables: { id }, fetchPolicy: 'no-cache' })
          .valueChanges.pipe(
            filter((result) => !result.loading),
            map((data) => data.data.getWorkplace),
            map((params) =>
              fields.map((field) => ({ ...field, value: (params as any)[field.name] })),
            ),
          );
      case 'CustomFieldDefinition':
        return this.getCustomField
          .watch({ variables: { id }, fetchPolicy: 'no-cache' })
          .valueChanges.pipe(
            filter((result) => !result.loading),
            map((data) => data.data.getCustomFieldDefinition),
            map((params) =>
              fields.map((field) => ({ ...field, value: (params as any)[field.name] })),
            ),
          );
      default:
        return of([]);
    }
  }
}
